import type { FundamentalExerciseOutput, FundamentalMode } from '@jeraisonne/ai';
import type { SubjectSlug, SchoolLevel } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';
import { GenerateError, type GenerateErrorCode, generateErrorMessage } from './generate-client';

export type GenerateFundamentalParams = {
  chapter_id: string;
  lesson_id?: string | null;
  subject: SubjectSlug;
  level: SchoolLevel;
  difficulty?: 'easy' | 'medium' | 'hard';
  question_count?: number;
  mode?: FundamentalMode;
  custom_instructions?: string;
  api_key?: string;
};

export type GenerateFundamentalResult = {
  output: FundamentalExerciseOutput;
  meta: {
    model: string;
    promptVersion: string;
    inputTokens: number;
    outputTokens: number;
  };
};

/**
 * Génère un exercice fondamental (banque de questions QCM indépendantes) via
 * l'Edge Function `generate-fundamental-exercise`. Réutilise GenerateError /
 * generateErrorMessage de generate-client pour une gestion d'erreur cohérente.
 */
export async function generateFundamentalExercise(
  params: GenerateFundamentalParams,
): Promise<GenerateFundamentalResult> {
  const { data, error } = await supabase.functions.invoke('generate-fundamental-exercise', {
    body: params,
  });

  if (error) {
    console.error('[generate-fundamental-exercise] network/invoke error:', error.message, error);
    throw new GenerateError('NETWORK_ERROR', error.message);
  }

  if (data?.error) {
    console.error(
      '[generate-fundamental-exercise] server error:',
      data.error,
      data.message,
      data.rawOutput?.slice(0, 500),
    );
    throw new GenerateError(
      data.error as GenerateErrorCode,
      data.message ?? 'Erreur de génération',
      data.rawOutput,
    );
  }

  if (!data?.output) {
    throw new GenerateError('EMPTY_RESPONSE', 'Réponse vide du serveur');
  }

  return data as GenerateFundamentalResult;
}

export { GenerateError, generateErrorMessage };
export type { GenerateErrorCode };
